import React from 'react';
import { FiCamera, FiVideo } from 'react-icons/fi';
import { ContentSubmissionUnit } from '@foodfluence/shared';
import deliverableNames from 'utils/deliverableNames';
import StatusCard from './StatusCard';
import { contentCardStatus } from '../utils/submissionCards';

/**
 * Names the platforms still waiting on a link, so the card reads
 * "Submit Instagram Reel link" rather than a generic "Submit post link".
 * Three or more platforms would overflow the card, so those get a count.
 */
function getSubmitLinkLabel(channels: string[]): string {
  const names = channels.map(
    key => deliverableNames[key as keyof typeof deliverableNames]?.singular ?? key
  );
  if (names.length === 1) return `Submit ${names[0]} link`;
  if (names.length === 2) return `Submit ${names[0]} + ${names[1]} links`;
  if (names.length > 2) return `Submit ${names.length} post links`;
  return 'Submit post link';
}

/**
 * Names what the influencer actually has to upload, so the card reads
 * "Upload 3 videos" rather than a generic "Upload content".
 */
function getUploadLabel(contentType?: 'video' | 'photo', count: number = 1): string {
  if (!contentType) return count > 1 ? `Upload ${count} pieces` : 'Upload content';
  return count > 1 ? `Upload ${count} ${contentType}s` : `Upload ${contentType}`;
}

function getContentSubLabel(status: string, uploadLabel: string): string {
  switch (status) {
    // Withdrawn by the brand. A voided unit keeps its original `status`, so without this the card
    // fell through to the default branch and told the creator to "Upload video" directly above a
    // caption saying it had been removed.
    case 'voided':
      return 'Removed by the brand — no longer needed';
    case 'approved':
      return 'Completed';
    case 'pendingVerification':
      return 'Under review';
    case 'contentRejected':
      return 'Needs edits';
    case 'linkPendingSubmission':
      return 'Submit post link';
    case 'linkPendingVerification':
      return 'Post under review';
    case 'linkNeedsEdit':
      return 'Post link rejected';
    case 'pendingSubmission':
    default:
      return uploadLabel;
  }
}

interface Props {
  index: number;
  unit: ContentSubmissionUnit;
  contentType?: 'video' | 'photo';
  name?: string;
  statusOverride?: string;
  /** Deliverable keys still awaiting a live post link, used to name them on the card. */
  outstandingChannels?: string[];
  /** How many pieces this card covers, so the upload label can say "Upload 3 videos". */
  contentCount?: number;
  onClick: () => void;
  /** Withdrawn by the brand: dimmed, unclickable, and styled as removed rather than as work. */
  isVoided?: boolean;
  /** Edit note shown inline on the card (brand revision note or rejected-link reason). */
  feedback?: string | null;
  feedbackFrom?: string;
}

const ContentUnitCard: React.FC<Props> = ({ index, unit, contentType, name, statusOverride, outstandingChannels, contentCount, onClick, isVoided = false, feedback, feedbackFrom }) => {
  const effectiveStatus = isVoided ? 'voided' : (statusOverride ?? (unit.contentRejected ? 'contentRejected' : unit.status));
  const sub =
    effectiveStatus === 'linkPendingSubmission' && outstandingChannels?.length
      ? getSubmitLinkLabel(outstandingChannels)
      : getContentSubLabel(effectiveStatus, getUploadLabel(contentType, contentCount));
  const ContentIcon = contentType === 'video' ? FiVideo : FiCamera;
  const displayName = name || (contentType === 'video' ? 'Video' : contentType === 'photo' ? 'Photo' : `Content Piece #${index + 1}`);
  const showFeedback = effectiveStatus === 'contentRejected' || effectiveStatus === 'linkNeedsEdit';

  return (
    <StatusCard
      icon={ContentIcon}
      title={displayName}
      sub={sub}
      status={contentCardStatus(effectiveStatus)}
      feedback={showFeedback ? feedback : null}
      feedbackFrom={feedbackFrom}
      onClick={onClick}
      isInactive={isVoided}
      smallIcon
    />
  );
};

export default React.memo(ContentUnitCard);
